// ReferenceDelete — the reference values' Delete (FRONTEND-PATTERNS §Reference-data pages): a
// type · a priority · a category · a tag · a responsibility · a zone · a cluster · an area is
// deleted for real, and only while NOTHING uses it. Two doors, one dialog:
//   • the five flat lists — Delete INSIDE the Manage drawer: `ReferenceDeleteDoor`, a quiet
//     danger link under the fields with its one line; the drawer renders the dialog as its
//     SIBLING (never inside the drawer — closing the drawer on success must not unmount it);
//   • zones · clusters · areas — Delete joins their Actions menu (danger, the lifecycle group),
//     the menu rendering the dialog.
// `useReferenceDelete` holds the dialog: `ask(value)` opens it on that value (its name kept
// through the closing animation); the dialog asks, runs the delete and, when the server refuses
// (`409 REFERENCE_IN_USE`), says INSIDE the dialog what still uses the value — each kind with
// its count, in the reader's words and digits ("Still in use — 12 sites and 2 responsibility
// chains."). Any other failure reads its errors.json words.

import { useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { REFERENCE_USE_KINDS, ReferenceUsesSchema } from '@ems-app/schemas/common/reference-uses';
import { apiErrorCode, apiErrorDetails, apiErrorMessage } from '../../lib/apiError.js';
import { formatList, formatNumber } from '../../lib/format.js';
import { Button } from '../ui/Button.js';
import { ConfirmDialog } from '../ui/ConfirmDialog.js';

/** What a refused delete says: the uses read back by kind (`REFERENCE_IN_USE`), else the failure's own words. */
export function referenceDeleteRefusal(err: unknown, t: TFunction, tErrors: TFunction): string {
  if (apiErrorCode(err) === 'REFERENCE_IN_USE') {
    const uses = ReferenceUsesSchema.safeParse(apiErrorDetails(err).uses);
    if (uses.success) {
      const words = REFERENCE_USE_KINDS.flatMap((kind) => {
        const count = uses.data[kind];
        return count ? [t(`reference.uses.${kind}`, { count, countText: formatNumber(count) })] : [];
      });
      if (words.length > 0) return t('reference.inUse', { uses: formatList(words) });
    }
  }
  return apiErrorMessage(err, tErrors);
}

interface ReferenceDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  /** The value's name — the question names it. */
  name: string;
  /** Runs the delete and what follows it; a rejection stays in the dialog. */
  onDelete: () => Promise<void>;
}

function ReferenceDeleteDialog({ open, onClose, name, onDelete }: ReferenceDeleteDialogProps) {
  const { t } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      tone="danger"
      title={t('reference.deleteTitle', { name })}
      description={t('reference.deleteBody')}
      confirmLabel={t('reference.delete')}
      onConfirm={async () => {
        try {
          await onDelete();
        } catch (err) {
          throw new Error(referenceDeleteRefusal(err, t, tErrors));
        }
      }}
    />
  );
}

/** The delete dialog of one door: `ask` opens it on a value; render `dialog` once (a drawer: as its sibling).
 *  `remove` runs the delete and whatever follows it (the host closes its drawer or its peek). */
export function useReferenceDelete(remove: (id: number) => Promise<void>): {
  ask: (value: { id: number; name: string }) => void;
  dialog: ReactNode;
} {
  const [target, setTarget] = useState<{ id: number; name: string } | null>(null);
  const [open, setOpen] = useState(false);
  return {
    ask: (value) => {
      setTarget({ id: value.id, name: value.name });
      setOpen(true);
    },
    dialog: (
      <ReferenceDeleteDialog
        open={open}
        onClose={() => setOpen(false)}
        name={target?.name ?? ''}
        onDelete={() => (target ? remove(target.id) : Promise.resolve())}
      />
    ),
  };
}

/** The Manage drawer's door — a quiet danger link under the fields, with its one line. */
export function ReferenceDeleteDoor({ onPress }: { onPress: () => void }) {
  const { t } = useTranslation('common');
  return (
    <div className="border-t border-[var(--color-border)] pt-4">
      <Button variant="link" tone="danger" onClick={onPress}>
        {t('reference.delete')}
      </Button>
      <p className="mt-1 text-xs text-[var(--color-text-secondary)]">{t('reference.deleteHint')}</p>
    </div>
  );
}
